import { DisabledFeatureConfig, Feature } from 'dashboard/models/verification-template-version';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';

export type DisabledFeatureConfigPanelForm = {
  // The index of the selected DisabledFeatureConfig in a VTV's external_disabled_feature_configs list.
  // -1 for new DisabledFeatureConfig
  index: number;
  // The following are the constituents of DisabledFeatureConfig.
  countryCode: CountryCode | 'default' | null;
  subdivision: string | 'default' | null;
  idClasses: GovernmentIdClass[] | 'default' | null;
  disabledFeatures: Feature[];
};

/**
 * Returns index of disabledFeatureConfigToFind in disabledFeatureConfigs, else -1 if not found.
 */
export const indexForOverride = ({
  disabledFeatureConfigs,
  disabledFeatureConfigToFind,
}: {
  disabledFeatureConfigs: DisabledFeatureConfig[];
  disabledFeatureConfigToFind: DisabledFeatureConfig | null;
}): number => {
  if (disabledFeatureConfigToFind == null) {
    return -1;
  }
  return disabledFeatureConfigs.findIndex((config: DisabledFeatureConfig) =>
    disabledFeatureConfigsAreEqual(disabledFeatureConfigToFind, config),
  );
};

function disabledFeatureConfigsAreEqual(
  value1: DisabledFeatureConfig,
  value2: DisabledFeatureConfig,
): boolean {
  return (
    value1['country-code'] === value2['country-code'] &&
    value1.subdivision === value2.subdivision &&
    idClassesAreEqual(value1['id-classes'], value2['id-classes'])
  );
}

function idClassesAreEqual(
  value1: GovernmentIdClass[] | 'default' | null,
  value2: GovernmentIdClass[] | 'default' | null,
): boolean {
  if (value1 === 'default' && value2 === 'default') {
    return true;
  }
  if (Array.isArray(value1) && Array.isArray(value2)) {
    return value1.length === value2.length && value1.every((item, index) => item === value2[index]);
  }
  return false; // One is 'default' and the other is an array, so they are not equal
}

export function sortDisabledFeatureConfigs(
  configs: DisabledFeatureConfig[],
): DisabledFeatureConfig[] {
  function rankSubdivisionIdClasses(config: DisabledFeatureConfig): number {
    const subdivision = config.subdivision;
    const idClasses = config['id-classes'];

    if (subdivision !== 'default' && idClasses !== 'default') {
      return 1;
    }
    if (subdivision === 'default' && idClasses !== 'default') {
      return 2;
    }
    if (subdivision !== 'default' && idClasses === 'default') {
      return 3;
    }
    if (subdivision === 'default' && idClasses === 'default') {
      return 4;
    }
    return 5;
  }

  return configs.sort((a, b) => {
    if (a['country-code'] == null || b['country-code'] == null) {
      return 0;
    }
    if (a['country-code'] === 'default' && b['country-code'] !== 'default') return 1;
    if (a['country-code'] !== 'default' && b['country-code'] === 'default') return -1;
    if (a['country-code'] !== 'default' && b['country-code'] !== 'default') {
      const countryCompare = a['country-code'].localeCompare(b['country-code']);
      if (countryCompare !== 0) return countryCompare;
    }
    return rankSubdivisionIdClasses(a) - rankSubdivisionIdClasses(b);
  });
}
